// from https://github.com/bsonntag/react-use-promise/blob/main/src/index.js

import { captureException } from '@sentry/browser';
import { useEffect, useReducer } from 'react';

const resolvePromise = <T>(
  promiseOrFactory: (() => Promise<T>) | Promise<T>,
): Promise<T> | undefined => {
  if (typeof promiseOrFactory === 'function') {
    return promiseOrFactory();
  }

  return promiseOrFactory;
};

const defaultState: State<unknown> = {
  error: undefined,
  result: undefined,
  state: 'pending',
};

type Action<T> =
  | {
      type: 'pending';
    }
  | {
      type: 'resolved';
      payload: T;
    }
  | {
      type: 'rejected';
      payload: Error;
    };

type State<T> =
  | {
      error: undefined;
      result: undefined;
      state: 'pending';
    }
  | {
      error: undefined;
      result: T;
      state: 'resolved';
    }
  | {
      error: Error;
      result: undefined;
      state: 'rejected';
    };

type PendingState = [undefined, undefined, 'pending'];
type ResolvedState<T> = [T, undefined, 'resolved'];
type RejectedState = [undefined, Error, 'rejected'];

type Resolution<T> = PendingState | ResolvedState<T> | RejectedState;

const reducer = <T>(state: State<T>, action: Action<T>): State<T> => {
  switch (action.type) {
    case 'pending':
      return {
        error: undefined,
        result: undefined,
        state: 'pending',
      };

    case 'resolved':
      return {
        error: undefined,
        result: action.payload,
        state: 'resolved',
      };

    case 'rejected':
      return {
        error: action.payload,
        result: undefined,
        state: 'rejected',
      };

    /* istanbul ignore next */
    default:
      return state;
  }
};

export const usePromise = <T>(
  promiseOrFactory: (() => Promise<T>) | Promise<T>,
  inputs: unknown[],
): Resolution<T> => {
  const [{ error, result, state }, dispatch] = useReducer<
    State<T>,
    [action: Action<T>]
  >(reducer, defaultState);

  useEffect(() => {
    const promise = resolvePromise(promiseOrFactory);

    if (!promise) {
      return;
    }

    let canceled = false;

    dispatch({ type: 'pending' });

    promise
      .then(
        (promiseResult) =>
          !canceled &&
          dispatch({
            payload: promiseResult,
            type: 'resolved',
          }),
        (promiseError: Error) =>
          !canceled &&
          dispatch({
            payload: promiseError,
            type: 'rejected',
          }),
      )
      .catch(captureException);

    return () => {
      canceled = true;
    };
    // biome-ignore lint/correctness/useExhaustiveDependencies: this is intended as it's a pass-through for the caller to control when the promise is re-resolved.
  }, inputs);

  return [result, error, state] as Resolution<T>;
};
